import React, { useState, useEffect } from "react";
import VerifyAdd from "./VerifyAdd";
import "./clinics.css";
import axios from "axios";
import { APIURL } from "../../../Global";
import { useAlert } from "react-alert";
import { FaToggleOff, FaToggleOn } from "react-icons/fa";

function FeaturesAdd(props) {
    const [show, setShow] = useState(false);
    const [purchasedFeatures, setPurchasedFeatures] = useState([]);
    const [remainingFeatures, setRemainingFeatures] = useState([]);
    const [amounts, setAmounts] = useState({});
    const [refresh, setRefresh] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [count, setCount] = useState(0);

    const user = JSON.parse(sessionStorage.getItem("logindatas"));
    const alert = useAlert();

    // Only the features that are eligible to be added (no pending/purchased status)
    const addableFeatures = remainingFeatures.filter(
        (item) =>
            !(item.payment_status && item.payment_status !== "not_paid") &&
            !(item.payment_status === "not_paid" && item.status === "accepted") &&
            item.status !== "pending"
    );

    const allSelected =
        addableFeatures.length > 0 &&
        addableFeatures.every((f) => selectedIds.has(f.features_id));

    useEffect(() => {
        sessionStorage.removeItem("counterData");
    }, []);

    useEffect(() => {
        const checkAndClearStatus = () => {
            const expiry = sessionStorage.getItem("status_expiry");
            if (expiry && Date.now() > parseInt(expiry, 10)) {
                sessionStorage.removeItem("status");
                sessionStorage.removeItem("status_expiry");
            }
        };
        checkAndClearStatus();
        const interval = setInterval(checkAndClearStatus, 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const tokenString = sessionStorage.getItem("usertoken");
        let authToken = tokenString.replace(/[\"]+/g, "");

        const fetchFeatures = axios.get(
            `${APIURL}/api/v1/staff/special-feature/?requested_user=${user.user_type}`,
            { headers: { Authorization: "Token " + authToken } }
        );
        const fetchPurchases = axios.get(
            `${APIURL}/api/v1/staff/special-feature/?requested_user=${user.user_type}&hospital_id=${props.id}`,
            { headers: { Authorization: "Token " + authToken } }
        );

        Promise.all([fetchFeatures, fetchPurchases])
            .then(([featuresRes, purchasesRes]) => {
                if (featuresRes.status === 200 && purchasesRes.status === 200) {
                    const featuresList = featuresRes.data.message || [];
                    const userFeaturesList = purchasesRes.data.data || [];
                    setPurchasedFeatures(userFeaturesList);
                    const purchasedFeatureNames = new Set(userFeaturesList.map((item) => item.features));
                    const remaining = featuresList.filter(
                        (feature) => !purchasedFeatureNames.has(feature.features__feature_name)
                    );
                    setRemainingFeatures(remaining);
                    setSelectedIds(new Set()); // reset selection on refresh
                }
            })
            .catch((err) => console.error("Error fetching data:", err));
    }, [refresh]);

    const incrementCounter = () => {
        const newCount = count + 1;
        setCount(newCount);
        sessionStorage.setItem("counterData", JSON.stringify({ count: newCount, timestamp: Date.now() }));
        setTimeout(() => {
            sessionStorage.removeItem("counterData");
            setCount(0);
        }, 60000);
    };

    // Toggle a single checkbox
    const toggleSelect = (featureId) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.has(featureId) ? next.delete(featureId) : next.add(featureId);
            return next;
        });
    };

    const handleSelectAll = () => {
        setSelectedIds(new Set(addableFeatures.map((f) => f.features_id)));
    };

    const handleClearAll = () => {
        setSelectedIds(new Set());
    };

    // Called after OTP is verified (or if session already verified)
    const confirmOTPBatch = (featuresToAdd) => {
        if (count >= 4) {
            alert.error("Try again after a minute");
            return;
        }

        setLoading(true);
        const tokenString = sessionStorage.getItem("usertoken");
        let v2 = tokenString.replace(/[\"]+/g, "");

        const requests = featuresToAdd.map((feature) => {
            const rawData = {
                requested_user: user.user_type,
                hospital_id: +props.id,
                feature_id: feature?.features_id,
                feature_price: Number(feature.price),
                custom_price:
                    amounts[feature.features_id] === undefined
                        ? feature.price
                        : Number(amounts[feature.features_id]),
                payment_status: feature.payment_status,
                start_date: feature.start_date,
                end_date: feature.end_date,
                created_by: user.user_id,
            };
            const data = Object.fromEntries(
                Object.entries(rawData).filter(([_, value]) => value !== null)
            );
            return axios.post(`${APIURL}/api/v1/staff/special-feature/`, data, {
                headers: { Authorization: "Token " + v2 },
            });
        });

        Promise.allSettled(requests)
            .then((results) => {
                const failedFeatures = featuresToAdd.filter(
                    (_, i) =>
                        results[i].status === "rejected" ||
                        results[i].value?.data?.status !== "success"
                );
                const succeeded = featuresToAdd.length - failedFeatures.length;

                if (succeeded > 0) {
                    alert.success(`${succeeded} ${succeeded === 1 ? "feature" : "features"} added successfully.`);
                    incrementCounter();
                }
                if (failedFeatures.length > 0) {
                    const names = failedFeatures
                        .map((f) =>
                            f.features__feature_name
                                ?.replace(/_/g, " ")
                                .replace(/\b\w/g, (c) => c.toUpperCase()) || "Unknown"
                        )
                        .join(", ");
                    alert.error(`Failed to add: ${names}`);
                }
                setRefresh((r) => !r);
            })
            .finally(() => setLoading(false));

        setShow(false);
    };

    const handleAddSelected = () => {
        if (selectedIds.size === 0) {
            alert.error("Please select at least one feature.");
            return;
        }
        const featuresToAdd = addableFeatures.filter((f) => selectedIds.has(f.features_id));
        const status = sessionStorage.getItem("status");
        if (status) {
            confirmOTPBatch(featuresToAdd);
        } else {
            setShow(true);
        }
    };

    const handleVerifySuccess = () => {
        const expirationTime = Date.now() + 30 * 60 * 1000;
        sessionStorage.setItem("status", "user verified");
        sessionStorage.setItem("status_expiry", expirationTime.toString());
        const featuresToAdd = addableFeatures.filter((f) => selectedIds.has(f.features_id));
        confirmOTPBatch(featuresToAdd);
    };

    const handleActivity = (item) => {
        const tokenString = sessionStorage.getItem("usertoken");
        let v2 = tokenString.replace(/[\"]+/g, "");
        const data = {
            requested_user: user.user_type,
            id: item.id,
            is_active: !item.is_active,
        };
        axios
            .put(`${APIURL}/api/v1/staff/special-feature/`, data, {
                headers: { Authorization: "Token " + v2 },
            })
            .then((res) => {
                if (res.data.status === "success") {
                    alert.success("Successfully updated");
                    setRefresh((r) => !r);
                } else {
                    alert.error(res.data.message);
                }
            })
            .catch(() => alert.error("Failed to update feature."));
    };

    const handleAmountChange = (featureId, value) => {
        const numericValue = Number(value);
        if (!isNaN(numericValue) && numericValue >= 0) {
            setAmounts((prev) => ({ ...prev, [featureId]: numericValue }));
        }
    };

    const formatName = (name) =>
        name?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "-";

    /* ─── Styles ─────────────────────────────────────── */
    const headerStyle = {
        display: "flex",
        alignItems: "center",
        backgroundColor: "rgba(85, 71, 159, 0.85)",
        color: "white",
        padding: "10px 14px",
        borderRadius: "8px 8px 0 0",
        fontWeight: 600,
        fontSize: "0.85rem",
        gap: 4,
    };

    const rowStyle = (isSelected) => ({
        display: "flex",
        alignItems: "center",
        padding: "8px 14px",
        borderLeft: "1px solid #dde1e7",
        borderRight: "1px solid #dde1e7",
        borderBottom: "1px solid #dde1e7",
        backgroundColor: isSelected ? "rgba(85, 71, 159, 0.06)" : "#fff",
        transition: "background 0.15s",
        fontSize: "0.86rem",
        gap: 4,
    });

    const amountInputStyle = {
        width: "80px",
        border: "1px solid #ccc",
        borderRadius: 6,
        padding: "3px 6px",
        fontSize: "0.83rem",
        outline: "none",
        textAlign: "center",
    };

    const checkboxStyle = {
        width: 16,
        height: 16,
        cursor: "pointer",
        accentColor: "rgba(85, 71, 159, 0.9)",
    };

    return (
        <div style={{ fontFamily: "inherit" }}>

            {/* ── Available Features Table ── */}
            {remainingFeatures.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                    {/* Table header */}
                    <div style={headerStyle}>
                        {/* Select-all checkbox */}
                        <div style={{ width: 36, display: "flex", justifyContent: "center", flexShrink: 0 }}>
                            <input
                                type="checkbox"
                                style={checkboxStyle}
                                checked={allSelected}
                                onChange={allSelected ? handleClearAll : handleSelectAll}
                                title={allSelected ? "Clear all" : "Select all"}
                            />
                        </div>
                        <div style={{ flex: 3 }}>Feature</div>
                        <div style={{ flex: 2, textAlign: "center" }}>Feature Amount</div>
                        <div style={{ flex: 2, textAlign: "center" }}>Custom Amount</div>
                        <div style={{ flex: 2, textAlign: "center" }}>Status</div>
                    </div>

                    {/* Feature rows */}
                    {remainingFeatures.map((item) => {
                        const isAddable =
                            !(item.payment_status && item.payment_status !== "not_paid") &&
                            !(item.payment_status === "not_paid" && item.status === "accepted") &&
                            item.status !== "pending";

                        const isChecked = selectedIds.has(item.features_id);

                        let statusNode;
                        if (item.payment_status && item.payment_status !== "not_paid") {
                            statusNode = <b style={{ color: "green" }}>Purchased</b>;
                        } else if (item.payment_status === "not_paid" && item.status === "accepted") {
                            statusNode = <b style={{ color: "green" }}>Purchased</b>;
                        } else if (item.status === "pending") {
                            statusNode = <b style={{ color: "#e67e22" }}>Pending</b>;
                        } else {
                            statusNode = (
                                <span
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 4,
                                        color: isChecked ? "rgba(85,71,159,0.9)" : "#888",
                                        fontSize: "0.8rem",
                                    }}
                                >
                                    {isChecked ? "✔ Selected" : "Not selected"}
                                </span>
                            );
                        }

                        return (
                            <div
                                key={item.features_id}
                                style={rowStyle(isChecked)}
                                onClick={() => isAddable && toggleSelect(item.features_id)}
                            >
                                <div style={{ width: 36, display: "flex", justifyContent: "center", flexShrink: 0 }}>
                                    {isAddable ? (
                                        <input
                                            type="checkbox"
                                            style={checkboxStyle}
                                            checked={isChecked}
                                            onChange={() => toggleSelect(item.features_id)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    ) : (
                                        <span style={{ width: 16 }} />
                                    )}
                                </div>
                                <div style={{ flex: 3 }}>{formatName(item.features__feature_name)}</div>
                                <div style={{ flex: 2, textAlign: "center" }}>{item.price || "-"}</div>
                                <div style={{ flex: 2, textAlign: "center" }}>
                                    {isAddable ? (
                                        <input
                                            type="number"
                                            style={amountInputStyle}
                                            value={amounts[item.features_id] ?? item.price}
                                            onChange={(e) => handleAmountChange(item.features_id, e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    ) : (
                                        <span>-</span>
                                    )}
                                </div>
                                <div style={{ flex: 2, textAlign: "center" }}>{statusNode}</div>
                            </div>
                        );
                    })}

                    {/* ── Action Bar ── */}
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginTop: 12,
                            padding: "10px 14px",
                            backgroundColor: "#f5f4fb",
                            borderRadius: 8,
                            border: "1px solid #ddd",
                        }}
                    >
                        {/* Select-all / Clear-all text buttons */}
                        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                            <button
                                onClick={handleSelectAll}
                                disabled={addableFeatures.length === 0}
                                style={{
                                    background: "none",
                                    border: "1px solid rgba(85,71,159,0.5)",
                                    color: "rgba(85,71,159,0.9)",
                                    borderRadius: 6,
                                    padding: "4px 12px",
                                    fontSize: "0.8rem",
                                    cursor: "pointer",
                                    fontWeight: 600,
                                }}
                            >
                                Select All
                            </button>
                            <button
                                onClick={handleClearAll}
                                disabled={selectedIds.size === 0}
                                style={{
                                    background: "none",
                                    border: "1px solid #bbb",
                                    color: "#666",
                                    borderRadius: 6,
                                    padding: "4px 12px",
                                    fontSize: "0.8rem",
                                    cursor: "pointer",
                                }}
                            >
                                Clear All
                            </button>
                            {selectedIds.size > 0 && (
                                <span style={{ fontSize: "0.8rem", color: "#555" }}>
                                    {selectedIds.size} feature{selectedIds.size > 1 ? "s" : ""} selected
                                </span>
                            )}
                        </div>

                        {/* Unified Add button */}
                        <button
                            onClick={handleAddSelected}
                            disabled={selectedIds.size === 0 || loading}
                            style={{
                                backgroundColor:
                                    selectedIds.size === 0 || loading
                                        ? "#ccc"
                                        : "rgba(85, 71, 159, 0.9)",
                                color: "white",
                                border: "none",
                                padding: "7px 20px",
                                borderRadius: 7,
                                fontWeight: 700,
                                fontSize: "0.88rem",
                                cursor:
                                    selectedIds.size === 0 || loading ? "not-allowed" : "pointer",
                                transition: "background 0.2s",
                                boxShadow:
                                    selectedIds.size > 0 && !loading
                                        ? "0 2px 8px rgba(85,71,159,0.3)"
                                        : "none",
                            }}
                        >
                            {loading
                                ? "Adding..."
                                : selectedIds.size > 0
                                ? `Add ${selectedIds.size} Feature${selectedIds.size > 1 ? "s" : ""}`
                                : "Add Selected"}
                        </button>
                    </div>
                </div>
            )}

            {/* ── Purchased Features Table ── */}
            {purchasedFeatures?.length > 0 ? (
                <div style={{ marginBottom: 10 }}>
                    <div style={{ ...headerStyle, borderRadius: "8px 8px 0 0" }}>
                        <div style={{ flex: 3 }}>Feature</div>
                        <div style={{ flex: 2, textAlign: "center" }}>Purchased Date</div>
                        <div style={{ flex: 2, textAlign: "center" }}>Feature Amount</div>
                        <div style={{ flex: 2, textAlign: "center" }}>Custom Amount</div>
                        <div style={{ flex: 2, textAlign: "center" }}>Status</div>
                        <div style={{ flex: 1, textAlign: "center" }}>Activate</div>
                    </div>
                    {purchasedFeatures.map((item, index) => (
                        <div key={index} style={rowStyle(false)}>
                            <div style={{ flex: 3 }}>{formatName(item.features)}</div>
                            <div style={{ flex: 2, textAlign: "center" }}>{item.start_date || "-"}</div>
                            <div style={{ flex: 2, textAlign: "center" }}>{item.feature_price || "-"}</div>
                            <div style={{ flex: 2, textAlign: "center" }}>{item.custom_price || "-"}</div>
                            <div style={{ flex: 2, textAlign: "center" }}>
                                {item.payment_status !== "not_paid" ? (
                                    <b style={{ color: "green" }}>Purchased</b>
                                ) : (
                                    <b style={{ color: "#e67e22" }}>Pending</b>
                                )}
                            </div>
                            <div style={{ flex: 1, textAlign: "center" }}>
                                {item.is_active && item.payment_status !== "not_paid" ? (
                                    <FaToggleOn
                                        style={{ cursor: "pointer", height: 21, color: "green", width: 25 }}
                                        onClick={() => handleActivity(item)}
                                    />
                                ) : (
                                    <FaToggleOff
                                        style={{
                                            cursor: item.payment_status !== "not_paid" ? "pointer" : "not-allowed",
                                            height: 21,
                                            color: "#7e7676",
                                            width: 25,
                                        }}
                                        onClick={() => item.payment_status !== "not_paid" && handleActivity(item)}
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <label>No features are requested</label>
            )}

            {/* ── OTP Verify Modal ── */}
            {show && (
                <VerifyAdd
                    onSuccess={handleVerifySuccess}
                    show={show}
                    onHide={() => setShow(false)}
                />
            )}
        </div>
    );
}

export default FeaturesAdd;